/**
 * src/features/resilience-reactor/data/resilienceLogic.js
 * The Physics Engine for the Resilience Reactor.
 * * CORE MECHANIC: The "Energy Economy"
 * - Time is finite (24h).
 * - Energy is finite (100 Units).
 * - Activities have upfront costs and delayed returns (Buffs/Debuffs).
 */

import { 
    Dumbbell, Coffee, BookOpen, Smartphone, 
    Moon, Users, Briefcase, Gamepad2, ShoppingCart, 
    Zap, BatteryCharging, AlertTriangle 
  } from 'lucide-react';
  
  // --- 1. THE ENERGY ECONOMY (Activity Database) ---
  // "Activities are not just tasks; they are strategic investments."
  export const REACTOR_ACTIVITIES = {
    // --- HEALTH (High Cost / Resilience Buff) ---
    'gym': {
      id: 'gym',
      label: 'Gym Session',
      icon: Dumbbell,
      category: 'health',
      timeCost: 2, // Hours
      energyCost: 20, // High upfront effort
      moodDelta: 10,
      resilienceBuff: 15, // Reduces impact of future stress
      description: "High effort now, armor for later."
    },
    'meal_prep': {
      id: 'meal_prep',
      label: 'Cook / Meal Prep',
      icon: ShoppingCart,
      category: 'health',
      timeCost: 2,
      energyCost: 15,
      moodDelta: 5,
      resilienceBuff: 10,
      description: "Prevents the 'Takeout Tax' on your wallet and health."
    },
    'sleep_optimized': {
      id: 'sleep_optimized',
      label: '8 Hours Sleep',
      icon: Moon,
      category: 'health',
      timeCost: 8,
      energyCost: -100, // RESTORES Energy
      moodDelta: 20,
      resilienceBuff: 20,
      description: "The ultimate reset button."
    },
  
    // --- SOCIAL (The "Third Place" Training) ---
    'social_high_friction': {
      id: 'social_party',
      label: 'Host a Party',
      icon: Users,
      category: 'social',
      timeCost: 4,
      energyCost: 30, // Planning is exhausting
      moodDelta: 25,
      resilienceBuff: 5,
      socialBatteryCharge: 100,
      description: "High effort, high social reward."
    },
    'social_low_friction': {
      id: 'social_hang',
      label: 'Run Club / Hobby Group',
      icon: Users,
      category: 'social',
      timeCost: 2,
      energyCost: 10, // "Third Places" are lower friction
      moodDelta: 15,
      resilienceBuff: 10,
      socialBatteryCharge: 60,
      description: "Recurring, low-stakes connection."
    },
  
    // --- GROWTH (Career Velocity vs. Mood Drain) ---
    'deep_work': {
      id: 'deep_work',
      label: 'Deep Work / Study',
      icon: BookOpen,
      category: 'growth',
      timeCost: 3,
      energyCost: 25,
      moodDelta: -5, // Hard work isn't always "fun"
      resilienceBuff: 5,
      careerVelocity: 20,
      description: "Focus is expensive but pays dividends."
    },
    'side_hustle': {
      id: 'side_hustle',
      label: 'Side Hustle',
      icon: Briefcase,
      category: 'growth',
      timeCost: 2,
      energyCost: 20,
      moodDelta: 5, // Passion project boost
      resilienceBuff: 0,
      careerVelocity: 15,
      description: "Building your empire after hours."
    },
  
    // --- DEPLETION (The "Lethargy Debuff") ---
    'doomscroll': {
      id: 'doomscroll',
      label: 'Doomscrolling',
      icon: Smartphone,
      category: 'depletion',
      timeCost: 2,
      energyCost: 5, // Low upfront cost (easy to start)
      moodDelta: -15, // Makes you feel worse
      resilienceBuff: -10, // Makes you weaker
      lethargyDebuff: true, // "Making future tasks harder to start"
      description: "Cheap dopamine, expensive consequences."
    },
    'binge_watch': {
      id: 'binge_watch',
      label: 'Binge Watching',
      icon: Gamepad2,
      category: 'depletion',
      timeCost: 4,
      energyCost: 5,
      moodDelta: 5, // Temporary relief
      resilienceBuff: -5,
      lethargyDebuff: true,
      description: "Time travel to 2 AM with nothing to show for it."
    }
  };
  
  // --- 2. THE ENTROPY ENGINE (Random Chaos) ---
  export const ENTROPY_SCENARIOS = [
    {
      id: 'crisis_work',
      title: 'The 5 PM Slack Ping',
      description: 'Your boss needs a complete deck redo by tomorrow morning.',
      impact: {
        timeLost: 4, // "Requiring five hours of overtime"
        energyDrain: 40,
        type: 'career'
      },
      mitigation: 'deep_work' // Having scheduled deep work helps absorb this
    },
    {
      id: 'crisis_domestic',
      title: 'Appliance Failure',
      description: 'The washing machine flooded the kitchen.',
      impact: {
        timeLost: 3,
        energyDrain: 30,
        type: 'life'
      },
      mitigation: 'meal_prep' // Having chores under control helps
    },
    {
      id: 'crisis_social',
      title: 'Last Minute Cancel',
      description: 'Your dinner plans fell through. You are alone with your thoughts.',
      impact: {
        timeLost: 0,
        energyDrain: 15, // Emotional toll
        moodHit: 20,
        type: 'social'
      },
      mitigation: 'social_low_friction' // Having a backup group helps
    }
  ];
  
  /**
   * --- NEW: BLIND AUDIT ALGORITHM ---
   * Converts raw hours into psychometric scores for the AI Analysis.
   * @param {Object} inputs - { sleepHours, phoneHours, workHours, socialCount }
   * @returns {Object} { energy, mood, resilience, flags }
   */
  export const calculateBaselineMetrics = (inputs) => {
    let energy = 100;
    let mood = 80;
    let resilience = 50;
    let riskFlags = [];

    const { sleepHours, phoneHours, workHours, socialCount } = inputs;

    // 1. SLEEP CALC (Base Recovery)
    if (sleepHours < 6) {
        energy -= 40;
        resilience -= 20;
        riskFlags.push("Sleep Deprivation (CRITICAL)");
    } else if (sleepHours < 8) {
        energy -= 15;
        riskFlags.push("Sleep Deficit");
    }

    // 2. DIGITAL DRAIN (The Dopamine Tax)
    if (phoneHours > 4) {
        energy -= (phoneHours * 5); // 5% drain per hour
        mood -= (phoneHours * 4);
        riskFlags.push("Digital Overload");
    }
    if (phoneHours > 6) {
        riskFlags.push("Dopamine Burnout Risk");
    }

    // 3. WORK LOAD (Capacity)
    if (workHours > 9) {
        energy -= 20;
        mood -= 10;
        riskFlags.push("Overwork / Crunch Mode");
    }

    // 4. SOCIAL BATTERY (Isolation Risk)
    if (socialCount === 0) {
        mood -= 20;
        riskFlags.push("Isolation Risk");
    } else if (socialCount > 4) {
        // Assume High Friction if excessive events
        energy -= 20; 
        riskFlags.push("Social Battery Drain");
    }

    // Clamp Values
    return {
        stats: {
            energy: Math.max(0, Math.min(100, energy)),
            mood: Math.max(0, Math.min(100, mood)),
            resilience: Math.max(0, Math.min(100, resilience))
        },
        riskFlags
    };
  };

  /**
   * Calculates the result of a "Day Simulation" (Stage 3)
   * @param {Object} currentStats - Current energy, mood, resilience
   * @param {Array} selectedActivityIds - List of activity IDs chosen by user
   * @returns {Object} New stats and feedback
   */
  export const calculateDayResult = (currentStats, selectedActivityIds) => {
    let { energy, mood, resilience, careerVelocity, socialBattery } = currentStats;
    let timeUsed = 0; // Standard work day assumption (8h fixed) + activities
    const TIME_CAP = 16; // 24h - 8h sleep (Sleep is an activity)
  
    let feedback = [];
    let lethargyTriggered = false;
  
    // 1. Process Activities
    selectedActivityIds.forEach(actId => {
      const act = REACTOR_ACTIVITIES[actId];
      if (!act) return;
  
      // Apply Costs
      energy -= act.energyCost;
      timeUsed += act.timeCost;
  
      // Apply Returns
      mood += (act.moodDelta || 0);
      resilience += (act.resilienceBuff || 0);
      
      if (act.socialBatteryCharge) socialBattery += act.socialBatteryCharge;
      if (act.careerVelocity) careerVelocity += act.careerVelocity;
      if (act.lethargyDebuff) lethargyTriggered = true;
    });
  
    // 2. Apply Lethargy Debuff
    if (lethargyTriggered) {
      energy -= 10; // Extra drain
      feedback.push("Lethargy Debuff Active: Starting tasks tomorrow will cost +10% Energy.");
    }
  
    // 3. Cap Values
    energy = Math.max(0, Math.min(100, energy));
    mood = Math.max(0, Math.min(100, mood));
    resilience = Math.max(0, Math.min(100, resilience));
    socialBattery = Math.max(0, Math.min(100, socialBattery));
  
    // 4. Burnout Check
    let status = 'stable';
    if (energy <= 10) {
      status = 'burnout';
      feedback.push("CRITICAL: Energy reserves depleted. You are running on adrenaline.");
    } else if (timeUsed > TIME_CAP) {
      status = 'overwhelmed';
      feedback.push(`Time Deficit: You tried to do ${timeUsed}h of stuff in a ${TIME_CAP}h day.`);
    }
  
    return {
      stats: { energy, mood, resilience, careerVelocity, socialBattery },
      timeUsed,
      status,
      feedback
    };
  };